/**
 * Shared labels/classes for portal + staff submission lists so screening workflow
 * matches coordinator-facing copy ("On screening hold", "Screening hold revision").
 */

import {
  getSubmissionEditPolicy,
  isSubmissionInActiveScreening,
  isSubmissionScreeningHold,
  resolveSubmissionEditPolicy,
  resolveSubmissionFinanciallyLocked,
  resolveSubmissionUnpaidProformaLinked,
} from './submissionEditPolicy';

export { isSubmissionInActiveScreening, isSubmissionScreeningHold };

export function canEditSubmission(submission) {
  if (submission?.editPolicy) return submission.editPolicy.canEdit;
  return resolveSubmissionEditPolicy(submission).canEdit;
}

export function canPortalEditSubmission(submission) {
  if (submission?.editPolicy) return submission.editPolicy.canEdit;
  return resolveSubmissionEditPolicy(submission).canEdit;
}

/**
 * Coordinators may upload signed PDFs when:
 * - voluntary: before application status becomes SUBMITTED (incl. tax invoice issued), or
 * - staff-requested: after SUBMITTED / during screening (PENDING_UPLOAD / RESUBMIT).
 * Blocked for rejected/archived applications.
 */
export function canPortalUploadSignedPdf(submission) {
  const status = String(submission?.status || '').toUpperCase();
  if (status === 'REJECTED' || status === 'ARCHIVED') return false;
  return true;
}

/** True once payment is complete and the app has entered screening (or later). */
export function isApplicationSubmittedOrLater(submission) {
  const status = String(submission?.status || '').toUpperCase();
  return ['SUBMITTED', 'REVIEWED', 'ACCEPTED', 'WAITLISTED', 'REJECTED', 'ARCHIVED'].includes(status);
}

/**
 * Voluntary signed PDF upload only — blocked from SUBMITTED onward.
 * Staff-requested uploads use canPortalUploadSignedPdf + pending signed-doc status.
 */
export function canPortalVoluntarilyUploadSignedPdf(submission) {
  if (!canPortalUploadSignedPdf(submission)) return false;
  return !isApplicationSubmittedOrLater(submission);
}

export function canPortalDeleteSubmission(submission) {
  if (submission?.editPolicy) return submission.editPolicy.canDelete;
  const financiallyLocked = resolveSubmissionFinanciallyLocked(submission);
  return getSubmissionEditPolicy(submission, { financiallyLocked }).canDelete;
}

export function canDeleteSubmission(submission) {
  return canPortalDeleteSubmission(submission);
}

export function getSubmissionEditTooltip(submission) {
  const policy = submission?.editPolicy || resolveSubmissionEditPolicy(submission, { staffChannel: true });
  if (policy.canEdit) {
    if (policy.editMode === 'staff_correction') return policy.message || 'Staff correction (judging / post-payment)';
    if (policy.editMode === 'limited') return policy.message;
    return 'Edit application';
  }
  return policy.message || 'This application cannot be edited.';
}

export function getSubmissionDeleteTooltip(submission) {
  const financiallyLocked = resolveSubmissionFinanciallyLocked(submission);
  const policy = submission?.editPolicy
    || getSubmissionEditPolicy(submission, {
      financiallyLocked,
      unpaidProformaLinked: resolveSubmissionUnpaidProformaLinked(submission),
    });
  if (policy.canDelete) {
    if (policy.deleteRequiresProformaCancel) {
      return 'Delete application (cancels the proforma and resets sibling applications)';
    }
    return 'Delete application';
  }
  if (financiallyLocked || policy.code === 'SUBMISSION_FINANCIALLY_LOCKED') {
    return 'This application is locked because payment was approved or a tax invoice was issued.';
  }
  return policy.message || 'Applications can only be deleted while pending and not linked to a proforma invoice.';
}

export function getProformaCancelDeleteMessage(submission, { bulkCount = 1 } = {}) {
  const base = submission?.editPolicy?.proformaCancelMessage
    || 'This will cancel the entire proforma request and reset all other linked applications to Pending.';
  if (bulkCount > 1) {
    return `${base} This applies to all selected applications on unpaid proforma batches.`;
  }
  const appNo = submission?.applicationNumber ? ` ${submission.applicationNumber}` : '';
  return `Delete application${appNo}? ${base}`;
}

export function getProformaCancelEditMessage() {
  return 'Saving these pricing changes will cancel the entire proforma request and reset all other linked applications to Pending. Continue?';
}

export function isScreeningHoldRevisionPending(submission) {
  return submission?.screeningHoldRevision?.requiresProforma === true;
}

/** Row label: revision pending overrides active screening hold, then workflow status. */
export function getWorkflowStatusLabel(submission) {
  if (isScreeningHoldRevisionPending(submission)) return 'Screening hold revision';
  if (isSubmissionScreeningHold(submission)) return 'On screening hold';
  const status = String(submission?.status || '').toUpperCase();
  const map = {
    PENDING: 'Pending',
    PROFORMA_REQUESTED: 'Proforma requested',
    PROFORMA_ISSUED: 'Proforma issued',
    INVOICE_ISSUED: 'Invoice issued',
    TAX_INVOICE_ISSUED: 'Tax invoice issued',
    SUBMITTED: 'Submitted',
    REVIEWED: 'Reviewed',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    ARCHIVED: 'Archived',
    WAITLISTED: 'Waitlisted',
  };
  return map[status] || submission?.status || '—';
}

export function getWorkflowStatusBadgeClass(submission) {
  if (isScreeningHoldRevisionPending(submission)) {
    return 'bg-orange-100 text-orange-900 border-orange-300';
  }
  if (isSubmissionScreeningHold(submission)) {
    return 'bg-amber-100 text-amber-950 border-amber-400';
  }
  const status = String(submission?.status || '').toUpperCase();
  const colors = {
    PENDING: 'bg-amber-100 text-amber-900 border-amber-200',
    PROFORMA_REQUESTED: 'bg-violet-100 text-violet-900 border-violet-200',
    PROFORMA_ISSUED: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    INVOICE_ISSUED: 'bg-sky-100 text-sky-900 border-sky-200',
    TAX_INVOICE_ISSUED: 'bg-teal-100 text-teal-900 border-teal-200',
    SUBMITTED: 'bg-blue-100 text-blue-800 border-blue-200',
    REVIEWED: 'bg-purple-100 text-purple-800 border-purple-200',
    ACCEPTED: 'bg-green-100 text-green-800 border-green-200',
    REJECTED: 'bg-red-100 text-red-800 border-red-200',
    ARCHIVED: 'bg-gray-100 text-gray-800 border-gray-200',
    WAITLISTED: 'bg-slate-100 text-slate-800 border-slate-200',
  };
  return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
}
